import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Perfil.css';

const Perfil = () => {
    const { user, updateUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const tabInicial = searchParams.get('tab') === 'password' ? 'password' : 'datos';
    const [tab, setTab] = useState(tabInicial);

    // Datos personales
    const [formData, setFormData] = useState({
        nombre: '',
        apellido: '',
        telefono: '',
        domicilio: ''
    });
    const [loadingDatos, setLoadingDatos] = useState(false);
    const [msgDatos, setMsgDatos] = useState({ tipo: '', texto: '' });

    // Cambio de contraseña
    const [passData, setPassData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loadingPass, setLoadingPass] = useState(false);
    const [msgPass, setMsgPass] = useState({ tipo: '', texto: '' });

    // Cargar los datos del usuario al entrar
    useEffect(() => {
        if (user) {
            setFormData({
                nombre: user.nombre || '',
                apellido: user.apellido || '',
                telefono: user.telefono || '',
                domicilio: user.domicilio || ''
            });
        }
    }, [user]);

    // Sincronizar tab con la URL
    useEffect(() => {
        const t = searchParams.get('tab') === 'password' ? 'password' : 'datos';
        setTab(t);
    }, [searchParams]);

    const cambiarTab = (nuevoTab) => {
        setSearchParams({ tab: nuevoTab });
        setMsgDatos({ tipo: '', texto: '' });
        setMsgPass({ tipo: '', texto: '' });
    };

    // Volver al panel anterior

    const handleVolver = () => {
        // Si venimos con state.from, volvemos a esa ruta específica
        const from = location.state?.from;
        if (from) {
            navigate(from);
            return;
        }

        // Fallback: volver al panel según el rol
        if (user?.role === 'admin') navigate('/admin/usuarios');
        else if (user?.role === 'masajista') navigate('/masajista/turnos');
        else if (user?.role === 'paciente') navigate('/paciente/nuevo-turno');
        else navigate('/login');
    };

    // Guardar datos personales
    const handleGuardarDatos = async (e) => {
        e.preventDefault();
        setMsgDatos({ tipo: '', texto: '' });
        setLoadingDatos(true);
        try {
            const res = await api.put('/auth/profile', formData);
            setMsgDatos({ tipo: 'success', texto: 'Datos actualizados correctamente' });
            if (updateUser) updateUser(res.data.user);
        } catch (err) {
            setMsgDatos({
                tipo: 'danger',
                texto: err.response?.data?.message || 'Error al actualizar los datos'
            });
        } finally {
            setLoadingDatos(false);
        }
    };

    // Cambiar contraseña
    const handleCambiarPassword = async (e) => {
        e.preventDefault();
        setMsgPass({ tipo: '', texto: '' });

        if (passData.newPassword.length < 6) {
            setMsgPass({ tipo: 'danger', texto: 'La nueva contraseña debe tener al menos 6 caracteres' });
            return;
        }
        if (passData.newPassword !== passData.confirmPassword) {
            setMsgPass({ tipo: 'danger', texto: 'Las contraseñas no coinciden' });
            return;
        }

        setLoadingPass(true);
        try {
            await api.put('/auth/change-password', {
                currentPassword: passData.currentPassword,
                newPassword: passData.newPassword
            });
            setMsgPass({ tipo: 'success', texto: 'Contraseña actualizada correctamente' });
            setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setMsgPass({
                tipo: 'danger',
                texto: err.response?.data?.message || 'Error al cambiar la contraseña'
            });
        } finally {
            setLoadingPass(false);
        }
    };

    if (!user) {
        return (
            <div className="perfil-wrapper">
                <div className="perfil-box">
                    <div className="alert alert-warning">Debés iniciar sesión para ver tu perfil</div>
                    <button className="btn btn-primary" onClick={() => navigate('/login')}>
                        Ir al inicio de sesión
                    </button>
                </div>
            </div>
        );
    }

    const getInitials = () => {
        const n = user.nombre?.trim();
        const a = user.apellido?.trim();
        if (n && a) return `${n.charAt(0)}${a.charAt(0)}`.toUpperCase();
        if (n) return n.charAt(0).toUpperCase();
        if (user.email) return user.email.charAt(0).toUpperCase();
        return '?';
    };

    const getNombreCompleto = () => {
        const nombre = [user.nombre, user.apellido].filter(Boolean).join(' ').trim();
        return nombre || user.email;
    };

    return (
        <div className="perfil-wrapper">
            <div className="perfil-container">
                {/* Botón volver */}
                <button className="perfil-back-btn" onClick={handleVolver}>
                    <span className="back-icon">←</span>
                    Volver
                </button>

                {/* Encabezado */}
                <div className="perfil-header">
                    <div className="perfil-avatar">{getInitials()}</div>
                    <div className="perfil-header-info">
                        <h2>{getNombreCompleto()}</h2>
                        <p>{user.email}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="perfil-tabs">
                    <button
                        className={`perfil-tab ${tab === 'datos' ? 'active' : ''}`}
                        onClick={() => cambiarTab('datos')}
                    >
                        👤 Mis Datos
                    </button>
                    <button
                        className={`perfil-tab ${tab === 'password' ? 'active' : ''}`}
                        onClick={() => cambiarTab('password')}
                    >
                        🔑 Cambiar Contraseña
                    </button>
                </div>

                {/* Contenido */}
                <div className="perfil-content">
                    {tab === 'datos' && (
                        <form onSubmit={handleGuardarDatos} className="perfil-form">
                            <div className="perfil-row">
                                <div className="form-group">
                                    <label>Nombre</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Apellido</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.apellido}
                                        onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={user.email}
                                    disabled
                                />
                                <small className="text-muted">El email no se puede modificar</small>
                            </div>

                            <div className="form-group">
                                <label>Teléfono</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.telefono}
                                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                    placeholder="011 - 4444 - 5555"
                                />
                            </div>

                            {msgDatos.texto && (
                                <div className={`alert alert-${msgDatos.tipo}`}>{msgDatos.texto}</div>
                            )}

                            <div className="perfil-form-actions">
                                <button type="submit" className="btn btn-primary" disabled={loadingDatos}>
                                    {loadingDatos ? 'Guardando...' : 'Guardar cambios'}
                                </button>
                            </div>
                        </form>
                    )}

                    {tab === 'password' && (
                        <form onSubmit={handleCambiarPassword} className="perfil-form">
                            <div className="form-group">
                                <label>Contraseña actual</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={passData.currentPassword}
                                    onChange={(e) => setPassData({ ...passData, currentPassword: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Nueva contraseña</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={passData.newPassword}
                                    onChange={(e) => setPassData({ ...passData, newPassword: e.target.value })}
                                    required
                                    minLength={6}
                                    placeholder="Mínimo 6 caracteres"
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirmar nueva contraseña</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={passData.confirmPassword}
                                    onChange={(e) => setPassData({ ...passData, confirmPassword: e.target.value })}
                                    required
                                />
                            </div>

                            {msgPass.texto && (
                                <div className={`alert alert-${msgPass.tipo}`}>{msgPass.texto}</div>
                            )}

                            <div className="perfil-form-actions">
                                <button type="submit" className="btn btn-primary" disabled={loadingPass}>
                                    {loadingPass ? 'Cambiando...' : 'Cambiar contraseña'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Perfil;